import React, { useState, useEffect } from 'react';
import { adminStatsService } from '../../services/adminStatsService';
import { seasonManagementService } from '../../services/seasonManagementService';
import { logsService } from '../../services/logsService';
import { STAT_TYPES, STAT_COLUMNS } from '../../services/supabase';
import { useSeasonContext } from '../../context/SeasonContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../styles/StatsManager.css';

const STAT_COLUMN_MAP = {
  [STAT_TYPES.PASSING]: ['pos', 'name', 'team', 'cmp', 'att', 'cmp_pct', 'yrds', 'avg', 'td', 'int', 'pick6', 'fum', 'qbr'],
  [STAT_TYPES.RUSHING]: ['pos', 'name', 'team', 'att', 'yrds', 'tds', 'fum', 'long'],
  [STAT_TYPES.RECEIVING]: ['pos', 'name', 'team', 'rec', 'trgt', 'yrds', 'tds', 'fum', 'long'],
  [STAT_TYPES.BLOCKING]: ['team', 'abv', 'dp', 'snps', 'ska'],
  [STAT_TYPES.DEFENSE]: ['pos', 'name', 'team', 'tck', 'tfl', 'sck', 'sfty', 'ff', 'fr', 'frtd', 'int', 'pbu', 'td'],
  [STAT_TYPES.KICKING]: ['pos', 'name', 'team', 'xpm', 'xpa', 'fgm', 'fga']
};

const READ_ONLY_FIELDS = ['cmp_pct', 'avg', 'qbr'];

const StatsManager = () => {
  const { selectedSeason, availableSeasons, setSelectedSeason } = useSeasonContext();
  const { user, hasPermission } = useAdminAuth();
  const canEditStats = hasPermission('edit_stats');
  const [selectedStatType, setSelectedStatType] = useState(STAT_TYPES.PASSING);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [weeks, setWeeks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([{ id: 0, ...initializePlayerObject(STAT_TYPES.PASSING) }]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [existingStats, setExistingStats] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editMode, setEditMode] = useState(false);

  function initializePlayerObject(statType) {
    const obj = {};
    const cols = STAT_COLUMN_MAP[statType] || [];
    cols.forEach(col => {
      obj[col] = '';
    });
    return obj;
  }

  useEffect(() => {
    loadAvailableWeeks();
  }, []);

  useEffect(() => {
    const initialPlayer = { id: 0, ...initializePlayerObject(selectedStatType) };
    
    if (selectedStatType === STAT_TYPES.PASSING) {
      const metrics = calculatePassingMetrics(initialPlayer);
      initialPlayer.cmp_pct = metrics.cmp_pct;
      initialPlayer.avg = metrics.avg;
      initialPlayer.qbr = metrics.qbr;
    }

    setPlayers([initialPlayer]);
    setSelectedRecord(null);
    setEditMode(false);
    loadExistingStats();
  }, [selectedStatType, selectedWeek, selectedSeason]);

  const loadExistingStats = async () => {
    try {
      const result = await adminStatsService.getStatsForWeekAndSeason(selectedStatType, selectedWeek, selectedSeason.id);
      if (result.success && result.data) {
        setExistingStats(result.data);
      }
    } catch (err) {
      console.error('Error loading existing stats:', err);
    }
  };

  const loadAvailableWeeks = async () => {
    try {
      const weeks = await adminStatsService.getAllWeeksWithData();
      const weekArray = Array.isArray(weeks) && weeks.length > 0 ? weeks : [1, 2, 3, 4, 5];
      setWeeks(weekArray);
    } catch (err) {
      console.error('Error loading weeks:', err);
      setWeeks([1, 2, 3, 4, 5]);
    }
  };

  useEffect(() => {
    const loadTeams = async () => {
      if (!selectedSeason) return;
      try {
        const teamStandings = await seasonManagementService.getTeamsForSeason(selectedSeason.id);
        if (teamStandings && Array.isArray(teamStandings)) {
          const teamList = teamStandings.map(standing => standing.teams).filter(Boolean);
          setTeams(teamList);
        }
      } catch (err) {
        console.error('Error loading teams:', err);
        setTeams([]);
      }
    };
    loadTeams();
  }, [selectedSeason]);

  const handleAddPlayer = () => {
    const newPlayer = {
      id: Date.now(),
      ...initializePlayerObject(selectedStatType)
    };
    
    if (selectedStatType === STAT_TYPES.PASSING) {
      const metrics = calculatePassingMetrics(newPlayer);
      newPlayer.cmp_pct = metrics.cmp_pct;
      newPlayer.avg = metrics.avg;
      newPlayer.qbr = metrics.qbr;
    }

    setPlayers([...players, newPlayer]);
  };

  const handleRemovePlayer = (playerId) => {
    if (players.length > 1) {
      setPlayers(players.filter(p => p.id !== playerId));
    }
  };

  const calculatePassingMetrics = (player) => {
    const cmp = parseFloat(player.cmp) || 0;
    const att = parseFloat(player.att) || 0;
    const yrds = parseFloat(player.yrds) || 0;
    const td = parseFloat(player.td) || 0;
    const int = parseFloat(player.int) || 0;

    const cmpPct = att > 0 ? ((cmp / att) * 100).toFixed(1) : '';
    const avg = att > 0 ? (yrds / att).toFixed(2) : '';
    
    let qbr = '';
    if (att > 0) {
      const a = Math.max(0, Math.min(2.375, (cmp / att - 0.3) * 5));
      const b = Math.max(0, Math.min(2.375, (yrds / att - 3) * 0.25));
      const c = Math.max(0, Math.min(2.375, (td / att) * 20));
      const d = Math.max(0, Math.min(2.375, 2.375 - (int / att) * 25));
      
      qbr = (((a + b + c + d) / 6) * 100).toFixed(1);
    }

    return { cmp_pct: cmpPct, avg, qbr };
  };

  const handlePlayerChange = (playerId, field, value) => {
    const updatedPlayer = { ...players.find(p => p.id === playerId), [field]: value };
    
    if (selectedStatType === STAT_TYPES.PASSING && ['cmp', 'att', 'yrds', 'td', 'int'].includes(field)) {
      const metrics = calculatePassingMetrics(updatedPlayer);
      updatedPlayer.cmp_pct = metrics.cmp_pct;
      updatedPlayer.avg = metrics.avg;
      updatedPlayer.qbr = metrics.qbr;
    }

    setPlayers(players.map(player =>
      player.id === playerId ? updatedPlayer : player
    ));
  };

  const handleSelectRecord = (record) => {
    setSelectedRecord(record);
    setEditMode(true);
    const lowerCaseMapping = {
      'POS': 'pos',
      'NAME': 'name',
      'TEAM': 'team',
      'CMP': 'cmp',
      'ATT': 'att',
      'CMP%': 'cmp_pct',
      'YRDS': 'yrds',
      'TD': 'td',
      'INT': 'int',
      'PICK6': 'pick6',
      'FUM': 'fum',
      'AVG': 'avg',
      'QBR': 'qbr',
      'TDS': 'tds',
      'LONG': 'long',
      'REC': 'rec',
      'TRGT': 'trgt',
      'REC%': 'rec_pct',
      'LGO': 'lgo',
      'ABV': 'abv',
      'DP': 'dp',
      'SNPS': 'snps',
      'SKA': 'ska',
      'SAD': 'sad',
      'SAS': 'sas',
      'TCK': 'tck',
      'TFL': 'tfl',
      'SCK': 'sck',
      'SFTY': 'sfty',
      'FF': 'ff',
      'FR': 'fr',
      'FRTD': 'frtd',
      'PBU': 'pbu',
      'XPM': 'xpm',
      'XPA': 'xpa',
      'XP%': 'xp_pct',
      'FGM': 'fgm',
      'FGA': 'fga',
      'FG%': 'fg_pct',
      'K%': 'k_pct',
      'PNTS': 'pnts',
    };

    const denormalizedData = { id: record.id };
    Object.keys(record).forEach(key => {
      const dbCol = lowerCaseMapping[key] || key.toLowerCase();
      denormalizedData[dbCol] = record[key];
    });

    if (selectedStatType === STAT_TYPES.PASSING) {
      const metrics = calculatePassingMetrics(denormalizedData);
      denormalizedData.cmp_pct = metrics.cmp_pct;
      denormalizedData.avg = metrics.avg;
      denormalizedData.qbr = metrics.qbr;
    }

    setPlayers([denormalizedData]);
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    setLoading(true);
    const result = await adminStatsService.deleteStat(selectedStatType, recordId);

    if (result.success) {
      setMessage({ type: 'success', text: 'Record deleted successfully' });
      setSelectedRecord(null);
      setEditMode(false);
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'STATS_DELETE',
          {
            stat_type: selectedStatType,
            player_name: selectedRecord?.NAME,
            week: selectedWeek,
            record_id: recordId
          },
          selectedSeason?.name
        );
      }
      
      const initialPlayer = { id: 0, ...initializePlayerObject(selectedStatType) };
      if (selectedStatType === STAT_TYPES.PASSING) {
        const metrics = calculatePassingMetrics(initialPlayer);
        initialPlayer.cmp_pct = metrics.cmp_pct;
        initialPlayer.avg = metrics.avg;
        initialPlayer.qbr = metrics.qbr;
      }
      setPlayers([initialPlayer]);
      
      loadExistingStats();
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to delete record' });
    }

    setLoading(false);
  };

  const handleUpdateRecord = async (e) => {
    e.preventDefault();

    const requiredField = selectedStatType === STAT_TYPES.BLOCKING ? 'team' : 'name';
    if (!selectedRecord || players.length === 0 || !players[0][requiredField]) {
      setMessage({ type: 'error', text: 'Please fill in required fields' });
      return;
    }

    const newName = (players[0].name || '').toLowerCase();
    const oldName = (selectedRecord.NAME || '').toLowerCase();

    if (newName !== oldName) {
      const nameExists = existingStats.some(stat => 
        stat.id !== selectedRecord.id && 
        stat.NAME && 
        stat.NAME.toLowerCase() === newName
      );

      if (nameExists) {
        setMessage({ type: 'error', text: `Player "${players[0].name}" already has stats for this week` });
        return;
      }
    }

    setLoading(true);
    const updatedData = { ...players[0] };
    delete updatedData.id;

    const result = await adminStatsService.updateStats(selectedStatType, selectedRecord.id, updatedData);

    if (result.success) {
      setMessage({ type: 'success', text: 'Record updated successfully' });
      setSelectedRecord(null);
      setEditMode(false);
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'STATS_UPDATE',
          {
            stat_type: selectedStatType,
            player_name: players[0]?.name,
            week: selectedWeek,
            record_id: selectedRecord.id,
            fields_updated: Object.keys(updatedData).filter(key => updatedData[key] !== selectedRecord[key])
          },
          selectedSeason?.name
        );
      }
      
      const initialPlayer = { id: 0, ...initializePlayerObject(selectedStatType) };
      if (selectedStatType === STAT_TYPES.PASSING) {
        const metrics = calculatePassingMetrics(initialPlayer);
        initialPlayer.cmp_pct = metrics.cmp_pct;
        initialPlayer.avg = metrics.avg;
        initialPlayer.qbr = metrics.qbr;
      }
      setPlayers([initialPlayer]);
      
      setTimeout(() => loadExistingStats(), 500);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to update record' });
    }

    setLoading(false);
  };

  const handleCancelEdit = () => {
    const initialPlayer = { id: 0, ...initializePlayerObject(selectedStatType) };
    
    if (selectedStatType === STAT_TYPES.PASSING) {
      const metrics = calculatePassingMetrics(initialPlayer);
      initialPlayer.cmp_pct = metrics.cmp_pct;
      initialPlayer.avg = metrics.avg;
      initialPlayer.qbr = metrics.qbr;
    }

    setSelectedRecord(null);
    setEditMode(false);
    setPlayers([initialPlayer]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editMode) {
      return handleUpdateRecord(e);
    }

    if (!selectedSeason) {
      setMessage({ type: 'error', text: 'Please select a season first' });
      return;
    }

    const getRequiredField = () => {
      return selectedStatType === STAT_TYPES.BLOCKING ? 'team' : 'name';
    };
    
    const requiredField = getRequiredField();
    const validPlayers = players.filter(p => p[requiredField] && String(p[requiredField]).trim());
    
    if (validPlayers.length === 0) {
      const fieldDisplay = requiredField === 'team' ? 'team' : 'name';
      setMessage({ type: 'error', text: `Please add at least one player with a ${fieldDisplay}` });
      return;
    }

    const duplicateRecords = validPlayers.filter(player => {
      if (selectedStatType === STAT_TYPES.BLOCKING) {
        return existingStats.some(stat => stat.TEAM && stat.TEAM === player.team);
      } else {
        return existingStats.some(stat => stat.NAME && stat.NAME.toLowerCase() === (player.name || '').toLowerCase());
      }
    });

    if (duplicateRecords.length > 0) {
      const displayValues = selectedStatType === STAT_TYPES.BLOCKING 
        ? duplicateRecords.map(p => p.team).join(', ')
        : duplicateRecords.map(p => p.name).join(', ');
      setMessage({ type: 'error', text: `Record(s) already have stats for this week: ${displayValues}. Please edit existing records instead.` });
      return;
    }

    setLoading(true);

    const cleanedPlayers = validPlayers.map(p => {
      const cleaned = { ...p };
      delete cleaned.id;
      return cleaned;
    });

    const result = await adminStatsService.insertStats(selectedStatType, selectedWeek, cleanedPlayers, selectedSeason.id);

    if (result.success) {
      setMessage({ type: 'success', text: `Successfully saved ${validPlayers.length} player(s)` });
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'STATS_CREATE',
          {
            stat_type: selectedStatType,
            week: selectedWeek,
            players_count: validPlayers.length,
            player_names: validPlayers.map(p => selectedStatType === STAT_TYPES.BLOCKING ? p.team : p.name).join(', ')
          },
          selectedSeason?.name
        );
      }
      
      const initialPlayer = { id: 0, ...initializePlayerObject(selectedStatType) };
      if (selectedStatType === STAT_TYPES.PASSING) {
        const metrics = calculatePassingMetrics(initialPlayer);
        initialPlayer.cmp_pct = metrics.cmp_pct;
        initialPlayer.avg = metrics.avg;
        initialPlayer.qbr = metrics.qbr;
      }
      setPlayers([initialPlayer]);
      
      loadAvailableWeeks();
      loadExistingStats();
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to save stats' });
    }

    setLoading(false);
  };

  const columns = STAT_COLUMN_MAP[selectedStatType] || [];

  return (
    <div className="stats-manager">
      <h2>Stats Manager</h2>

      <div className="stats-controls">
        <div className="control-group">
          <label>Season</label>
          <select
            value={selectedSeason ? String(selectedSeason.id) : ''}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
          >
            {availableSeasons.map(season => (
              <option key={season.id} value={String(season.id)}>
                {season.name} {season.is_active ? '(Current)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>Stat Type</label>
          <select
            value={selectedStatType}
            onChange={(e) => setSelectedStatType(e.target.value)}
          >
            {Object.values(STAT_TYPES).map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>Week</label>
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
          >
            {[...Array(20)].map((_, i) => (
              <option key={i + 1} value={i + 1}>Week {i + 1}</option>
            ))}
          </select>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {existingStats.length > 0 && (
        <div className="existing-stats">
          <h3>Existing Records</h3>
          <div className="stats-list">
            {existingStats.map((stat) => (
              <div
                key={stat.id}
                className={`stat-item ${selectedRecord?.id === stat.id ? 'selected' : ''}`}
              >
                <div className="stat-info">
                  <strong>{selectedStatType === STAT_TYPES.BLOCKING ? stat.TEAM : stat.NAME}</strong> {selectedStatType === STAT_TYPES.BLOCKING ? `(${stat.ABV})` : `- ${stat.TEAM || 'N/A'}`}
                </div>
                <div className="stat-actions">
                  {canEditStats && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleSelectRecord(stat)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRecord(stat.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </>
                  )}
                  {!canEditStats && (
                    <span className="stats-read-only">Read-only (input only)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>

      <div className="players-table">
          <div className="table-header">
            {columns.map(col => (
              <div key={col} className="table-cell header-cell">
                {col.toUpperCase()}
              </div>
            ))}
            <div className="table-cell header-cell">ACTION</div>
          </div>

          {players.map((player, idx) => (
            <div key={player.id} className="table-row">
              {columns.map(col => (
                <div key={col} className="table-cell">
                  {READ_ONLY_FIELDS.includes(col) ? (
                    <div className="read-only-field">
                      {player[col] || '-'}
                    </div>
                  ) : col === 'team' ? (
                    <select
                      value={player[col] || ''}
                      onChange={(e) => handlePlayerChange(player.id, col, e.target.value)}
                      className="team-select"
                    >
                      <option value="">Select Team</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={col === 'name' || col === 'pos' || col === 'abv' ? 'text' : 'number'}
                      value={player[col] || ''}
                      onChange={(e) => handlePlayerChange(player.id, col, e.target.value)}
                      placeholder={col}
                      step={col === 'name' || col === 'pos' || col === 'abv' ? undefined : '0.01'}
                    />
                  )}
                </div>
              ))}
              <div className="table-cell action-cell">
                {players.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePlayer(player.id)}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="form-actions">
          {editMode ? (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Record'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleAddPlayer}
                className="btn btn-secondary"
              >
                + Add Player
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Stats'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default StatsManager;
